/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
'use client';
import { Button } from '@ahafood/ui/components/button';
import { Image } from '@ahafood/ui/components/image';
import { assetPrefix } from '@ahm/api-wrappers-core';
import { cn, JSFunction } from '@ahm/common-helpers';
import { getCookie } from 'cookies-next';
import { ArrowLeft, Search } from 'lucide-react';
import { useParams, useSearchParams } from 'next/navigation';
import { useCallback } from 'react';
import { HeaderBackground } from './header-background';
import SolomealHeader from './solomeal-header';
import NotificationDemo from '@/app/[locale]/(main)/store/components/store-notification';
import { LocationMismatchPopover } from '@/components/location-mismatch-popover';
import UserButton from '@/components/user/user-button';
import { useStoreCitySheet } from '@/contexts/store-city-sheet-context';
import { useCityRoutes } from '@/hooks/use-city-navigation';
import { usePathname, useRouter } from '@/i18n/routing';
import MainHeader from '@/layouts/main/main-header';
import { clearDebugLogs } from '@/lib/debug/use-debug-tracker';
import { trackEvent } from '@/lib/event-tracking';
import { prettifyCitySlug } from '@/lib/fast-location-utils';
import { useIsMobile } from '@/lib/is-mobile';
import {
  useDetectWebInApp,
  useWebInAppHydrated,
} from '@/lib/is-webinapp/use-detect-webinapp';
import { useAiFoodOrderStore, useAuthStore } from '@/store';
import { useLocationMismatchStore } from '@/store/use-location-mismatch-store';

/**
 * Lấy label địa chỉ từ cookie `ahafood_address`. Cookie do middleware ghi khi
 * xử `?lat&lng`, hoặc còn lại từ phiên trước.
 */
function readAddressLabelFromCookie(raw: unknown): string | undefined {
  if (typeof raw !== 'string' || !raw) return undefined;
  try {
    return (JSON.parse(raw) as { address?: string }).address?.trim();
  } catch {
    return undefined;
  }
}

/**
 * Tên city hiển thị trên header.
 *
 * Route rootless (`/`) không có segment city — city đang xem CHÍNH LÀ city của
 * địa chỉ, nên lấy thẳng tên đã lưu. Có segment thì chỉ dùng tên đã lưu khi nó
 * khớp slug, còn lại đẹp hoá từ slug.
 */
function resolveCityLabel(
  citySlug: string | undefined,
  storeCitySlug: string | null,
  storeCityName: string | null
): string | undefined {
  if (!citySlug) return storeCityName ?? undefined;
  if (storeCitySlug === citySlug && storeCityName) return storeCityName;
  return prettifyCitySlug(citySlug) || citySlug;
}

/**
 * Chọn label địa chỉ hiển thị + cờ hasAddress cho header.
 *
 * Ưu tiên: store → `?address` trên URL → cookie `ahafood_address` → tên city.
 * Cần cả hai nguồn first-paint vì có hai đường vào: middleware nuốt `?lat&lng`
 * rồi ghi cookie (đường chính, URL đã sạch param), còn khi middleware bị bỏ qua
 * thì `?address` vẫn nằm trên URL. `hasAddress` chỉ tính nguồn địa chỉ thật —
 * tên city không được coi là đã có địa chỉ.
 *
 * Tách ra module scope để giữ StoreHeader gọn (cognitive complexity).
 */
function deriveHeaderAddress(
  storeAddress: string | undefined,
  urlAddressParam: string | null,
  addressCookie: unknown,
  cityLabel: string | undefined
): { addressLabel: string | undefined; hasAddress: boolean } {
  const trimmed = urlAddressParam?.trim();
  const urlAddress = trimmed ? trimmed : undefined;
  const label =
    storeAddress ?? urlAddress ?? readAddressLabelFromCookie(addressCookie);

  return {
    addressLabel: label ?? cityLabel,
    hasAddress: Boolean(label),
  };
}

export default function StoreHeader() {
  const params = useParams();
  const searchParams = useSearchParams();
  const address = useAiFoodOrderStore.use.address();
  const storeCitySlug = useAiFoodOrderStore.use.city_slug();
  const storeCityName = useAiFoodOrderStore.use.city_name();
  // KHÔNG có segment city = route rootless (`/`) = đang xem city của chính mình.
  const citySlug = Array.isArray(params.city) ? params.city[0] : params.city;
  const isRootless = !citySlug;
  const cityLabel = resolveCityLabel(citySlug, storeCitySlug, storeCityName);

  const routes = useCityRoutes();
  const router = useRouter();
  const pathName = usePathname();
  const isMobile = useIsMobile();
  const isWebInApp = useDetectWebInApp();
  // Detection resolve sau mount → consumer cần defer render UserButton/BackButton
  // để tránh flash LogIn icon rồi biến mất khi flip sang WebInApp.
  const hydrated = useWebInAppHydrated();

  const addressCookie = getCookie('ahafood_address');

  const { openCitySheet } = useStoreCitySheet();
  const { user } = useAuthStore();
  const setPendingPrefill = useLocationMismatchStore.use.setPendingPrefill();

  const logBackButtonClick = () => {
    trackEvent('click_button', {
      action_time: Math.floor(Date.now() / 1000),
      button_name: 'is_back',
      button_location: 'home',
      timestamp: Date.now(),
      user_id: user?.cunstomerId,
    });
  };

  const handleToggleAmori = useCallback(() => {
    if (
      typeof window !== 'undefined' &&
      typeof window.setOpenChange === 'function'
    ) {
      window.setOpenChange(true);
      trackEvent('chatbot_event', {
        action_time: Math.floor(Date.now() / 1000),
        button_name: 'chatbot_banner',
        timestamp: Date.now(),
        user_id: user?.cunstomerId,
      });
    }
  }, [user?.cunstomerId]);

  // Dùng `routes.search` chứ không tự ghép slug: ở route rootless (`/`) không có
  // segment city, ghép tay sẽ ra `/undefined/...` — hoặc như trước là chặn luôn
  // (`if (!citySlug) return`) khiến nút tìm kiếm ở trang chủ bấm không ăn.
  const handleSearchClick = useCallback(() => {
    router.push(`${routes.search}?focus=1`);
  }, [router, routes.search]);

  // ĐÃ BỎ: tự bung dialog khi chưa có địa chỉ.
  //
  // Trang `/{city}` là chế độ DUYỆT — chạy hoàn toàn bằng `city_id` lấy từ slug,
  // không cần toạ độ. Ép nhập địa chỉ ở đây là chặn user ngay khi họ vừa mở link
  // một thành phố, đúng thứ luồng mới muốn bỏ.
  //
  // Địa chỉ chỉ bị đòi ở nơi thật sự cần: cổng giỏ hàng (estimate/create-order),
  // filter "Gần tôi", và các section xếp theo khoảng cách. Muốn nhập chủ động
  // thì bấm thẳng dòng địa chỉ trên header này.
  //
  // Trang chủ theo địa chỉ (`/`) không rơi vào đây: thiếu địa chỉ là nó đã
  // redirect sang `/{DEFAULT}` từ phía server.

  if (pathName.includes('/danh-sach-cua-hang')) {
    return null;
  }

  const isSolomealPage =
    pathName.includes('/solomeal') && !pathName.includes('/danh-sach-cua-hang');

  if (isSolomealPage) {
    return <SolomealHeader />;
  }

  if (!isMobile) {
    return <MainHeader />;
  }

  // Label cho first-paint, trước khi store kịp hydrate. Hai nguồn dự phòng vì
  // có hai đường vào: middleware nuốt `?lat&lng` rồi ghi cookie (URL đã sạch),
  // hoặc middleware bị bỏ qua thì `?address` còn nguyên trên URL.
  // Địa chỉ đã lưu chỉ được hiện khi nó thuộc đúng city đang duyệt. Hai trường
  // hợp vẫn coi là hợp lệ:
  //  - rootless (`/`): không có segment city, city đang xem CHÍNH LÀ city của
  //    địa chỉ. Thiếu nhánh này thì `storeCitySlug === undefined` ra false →
  //    header hiện "Chọn địa chỉ" dù user đã có địa chỉ.
  //  - chưa biết city của địa chỉ (user cũ, store chưa rehydrate) → thà hiện
  //    địa chỉ còn hơn nhấp nháy sang "Chọn địa chỉ" rồi quay lại.
  const addressBelongsToCity =
    isRootless || !storeCitySlug || storeCitySlug === citySlug;

  const { addressLabel, hasAddress } = deriveHeaderAddress(
    address?.address,
    searchParams.get('address'),
    addressCookie,
    cityLabel
  );

  return (
    <>
      <NotificationDemo />
      <HeaderBackground className="px-4 py-5">
        <header
          className={cn('mx-auto flex flex-col w-full max-w-screen-xl gap-4')}
        >
          {/* Address row — plain text, flex-col 2 dòng.
              WebInApp: back button bên trái, không user icon.
              Non-WebInApp: user icon ngoài cùng phải.
              Trước khi hydrated: skeleton placeholder để tránh flash. */}
          <div className="flex items-center gap-0">
            {hydrated && isWebInApp ? (
              <Button
                variant="ghost"
                size="icon"
                type="button"
                onClick={() => {
                  logBackButtonClick();
                  clearDebugLogs();
                  JSFunction.call({ name: 'close' });
                }}
                className="flex shrink-0 items-center -ml-2 justify-center rounded-full [&_svg]:size-6 hover:bg-transparent"
                aria-label="Đóng"
              >
                <ArrowLeft className="size-6 text-neutral-70" />
              </Button>
            ) : null}
            <LocationMismatchPopover
              side="bottom"
              align="start"
              onChangeAddress={(gps) => {
                setPendingPrefill(gps);
                openCitySheet();
              }}
            >
              <button
                type="button"
                onClick={() => openCitySheet()}
                className="flex flex-col items-start text-left py-1 -my-1 min-w-0 max-w-[75%]"
              >
                <span className="text-base text-neutral-70 font-normal leading-tight flex items-center gap-1">
                  <Image
                    src={`${assetPrefix}/static/icons/location.svg`}
                    alt=""
                    width={18}
                    height={18}
                    className="shrink-0"
                  />{' '}
                  Giao đến
                </span>
                <span className="flex items-center gap-0 w-full min-w-0">
                  <span className="text-base text-neutral-90 font-semibold leading-snug truncate">
                    {hasAddress ? addressLabel : 'Chọn địa chỉ'}
                  </span>
                </span>
              </button>
            </LocationMismatchPopover>
            {!hydrated ? null : !isWebInApp ? (
              <div className="ml-auto shrink-0">
                <UserButton />
              </div>
            ) : null}
          </div>

          {/* Search pill + Amori button, ngang hàng */}
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleSearchClick}
              className="flex items-center gap-3 h-12 flex-1 px-3 rounded-xl border bg-white"
              aria-label="Tìm món, tìm quán"
            >
              <Search className="shrink-0 size-5 text-neutral-60" />
              <span className="text-base font-normal text-neutral-50">
                Bạn đang thèm gì nào?
              </span>
            </button>
            <button
              type="button"
              onClick={handleToggleAmori}
              className="relative size-12 shrink-0 rounded-full overflow-hidden"
              aria-label="Mở Amori — trợ lý món ăn AI"
            >
              <Image
                src={`${assetPrefix}/static/images/amori-nav-avt.webp`}
                alt="Amori"
                fill
                className="object-cover"
              />
            </button>
          </div>
        </header>
      </HeaderBackground>
    </>
  );
}
